import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { geofenceApi } from '../services/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  XMarkIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { useUnitStore } from '../store/unitStore';
import { apiErrorMessage } from '../lib/apiError';
import type { GeofenceType } from '../types/domain';

// ST-2026 geofence taxonomy.
const GEOFENCE_TYPES = ['FARMBASE', 'TARGET', 'HIDEOUT', 'RV', 'BOUNDARY', 'CHECKPOINT'] as const;

// HQ authors these four through the create/draw tools.
// HIDEOUT + RV are dropped live from the field (socket) and are read-only here.
const CREATABLE_TYPES: GeofenceType[] = ['FARMBASE', 'TARGET', 'BOUNDARY', 'CHECKPOINT'];
const FIELD_TYPES: GeofenceType[] = ['HIDEOUT', 'RV'];
const isFieldType = (type: GeofenceType) => FIELD_TYPES.includes(type);

// Single source of truth for label / icon / color per geofence type.
// Full Tailwind class strings (no interpolation) so the JIT compiler keeps them.
const GEOFENCE_META: Record<
  GeofenceType,
  { label: string; emoji: string; badge: string; dot: string }
> = {
  FARMBASE:   { label: 'Farmbase',   emoji: '🏠', badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-500' },
  TARGET:     { label: 'Target',     emoji: '🎯', badge: 'bg-red-500/20 text-red-400 border-red-500/30',             dot: 'bg-red-500' },
  HIDEOUT:    { label: 'Hideout',    emoji: '🕳️', badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',       dot: 'bg-amber-500' },
  RV:         { label: 'RV',         emoji: '📍', badge: 'bg-rose-500/20 text-rose-400 border-rose-500/30',           dot: 'bg-rose-500' },
  BOUNDARY:   { label: 'Boundary',   emoji: '⬛', badge: 'bg-violet-500/20 text-violet-400 border-violet-500/30',    dot: 'bg-violet-500' },
  CHECKPOINT: { label: 'Checkpoint', emoji: '🚩', badge: 'bg-sky-500/20 text-sky-400 border-sky-500/30',             dot: 'bg-sky-500' },
};

interface CreateGeofenceForm {
  name: string;
  type: GeofenceType;
  description: string;
  centerLat: string;
  centerLon: string;
  radiusMeters: string;
  alertOnEntry: boolean;
  alertOnExit: boolean;
}

const initialForm: CreateGeofenceForm = {
  name: '',
  type: 'FARMBASE',
  description: '',
  centerLat: '',
  centerLon: '',
  radiusMeters: '100',
  alertOnEntry: false,
  alertOnExit: false,
};

function normalizeHexColor(hex: string | undefined | null): string | null {
  const h = (hex || '').trim();
  if (!h) return null;
  if (/^#[0-9a-f]{6}$/i.test(h)) return h;
  if (/^[0-9a-f]{6}$/i.test(h)) return `#${h}`;
  return null;
}

// Geofence Card Component
function GeofenceCard({
  geofence,
  onClick,
  onDelete,
  onEdit,
  canManage,
}: {
  geofence: any;
  onClick: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  canManage?: boolean;
}) {
  const { units } = useUnitStore();

  const type = geofence.type as GeofenceType;
  const meta = GEOFENCE_META[type] ?? GEOFENCE_META.BOUNDARY;
  const fieldDropped = isFieldType(type);

  const unit = geofence.unitId ? units.find((b) => b.id === geofence.unitId) : null;
  const unitColor = normalizeHexColor(unit?.aorColor) || '#374151';
  const unitCode = unit?.code || '';

  return (
    <div
      className={clsx(
        'bg-command-panel border rounded-xl hover:bg-command-panel/80 transition-all cursor-pointer group relative overflow-hidden',
        // Field-dropped pins get a distinct dashed treatment.
        fieldDropped
          ? 'border-dashed border-command-accent/40 hover:border-command-accent/70'
          : 'border-command-border hover:border-command-accent/50'
      )}
      onClick={onClick}
    >
      {/* Unit strip: represents the owning unit's AOR color */}
      {!!geofence.unitId && (
        <div className="h-1 w-full" style={{ backgroundColor: unitColor }} />
      )}
      <div className="p-4">
        {/* Action buttons (HQ only) */}
        {canManage && (onEdit || onDelete) && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-1.5 bg-blue-500/20 hover:bg-blue-500/40 rounded-lg"
                title={fieldDropped ? 'Rename geofence' : 'Edit geofence'}
              >
                <PencilSquareIcon className="w-4 h-4 text-blue-400" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg"
                title="Delete geofence"
              >
                <TrashIcon className="w-4 h-4 text-red-400" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center border', meta.badge)}>
              <span className="text-lg">{meta.emoji}</span>
            </div>
            <div>
              <p className="font-semibold text-gray-100 flex items-center gap-2">
                {geofence.name}
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </p>
              <p className="text-sm text-gray-500">
                {meta.label}
                {unitCode ? (
                  <span className="ml-2 text-[10px] font-semibold" style={{ color: unitColor }}>
                    {unitCode}
                  </span>
                ) : null}
              </p>
            </div>
          </div>
          {fieldDropped ? (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-command-accent/20 text-command-accent border border-command-accent/30">
              Field
            </span>
          ) : (
            <span className={clsx(
              'px-2 py-0.5 rounded text-xs',
              geofence.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
            )}>
              {geofence.isActive ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>

        {geofence.description && (
          <p className="mt-2 text-sm text-gray-500 line-clamp-2">{geofence.description}</p>
        )}

        {/* Coordinates */}
        {geofence.centerLat != null && geofence.centerLon != null && (
          <div className="mt-2 text-xs text-gray-600 font-mono">
            📍 {geofence.centerLat.toFixed(4)}°N, {geofence.centerLon.toFixed(4)}°E
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-command-border grid grid-cols-2 gap-2 text-xs">
          {geofence.radiusMeters ? (
            <div className="text-gray-500">
              Radius: <span className="text-gray-300">{geofence.radiusMeters}m</span>
            </div>
          ) : geofence.polygonCoords ? (
            <div className="text-gray-500">Shape: <span className="text-gray-300">Polygon</span></div>
          ) : null}
          <div className="text-gray-500">
            Entry Alert: <span className={geofence.alertOnEntry ? 'text-green-400' : 'text-gray-400'}>
              {geofence.alertOnEntry ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="text-gray-500">
            Exit Alert: <span className={geofence.alertOnExit ? 'text-green-400' : 'text-gray-400'}>
              {geofence.alertOnExit ? 'Yes' : 'No'}
            </span>
          </div>
        </div>

        {/* View on Map hint */}
        <div className="mt-3 text-xs text-command-accent opacity-0 group-hover:opacity-100 transition-opacity text-center">
          Click to view on Live Map →
        </div>
      </div>
    </div>
  );
}

export default function GeofencesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { getEffectiveUnitIds, isHQ } = useUnitStore();
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGeofence, setSelectedGeofence] = useState<any>(null);
  const [editingGeofence, setEditingGeofence] = useState<any>(null);
  const [form, setForm] = useState<CreateGeofenceForm>(initialForm);

  // All create / edit / delete tools are gated on HQ role (role-based, never unit code).
  const canManage = isHQ(user?.unitId ?? null);

  // Scope the query to a unit. Player-unit users are locked to their own unit;
  // HQ can view all units or a single picked unit.
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId ?? null);
  const unitIdForApi = canManage
    ? (effectiveUnitIds && effectiveUnitIds.length === 1 ? effectiveUnitIds[0] : undefined)
    : (user?.unitId ?? undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ['geofences', typeFilter, effectiveUnitIds],
    queryFn: async () => {
      const response = await geofenceApi.getAll({
        type: typeFilter || undefined,
        isActive: true,
        unitId: unitIdForApi,
      });
      return response.data;
    },
    retry: 2,
    retryDelay: 1000,
  });

  const createGeofenceMutation = useMutation({
    mutationFn: (payload: any) => {
      const { shape, ...data } = payload;
      return shape === 'polygon' ? geofenceApi.createPolygon(data) : geofenceApi.createCircle(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      toast.success('Geofence created successfully');
      setShowCreateModal(false);
      setForm(initialForm);
    },
    onError: (err: any) => {
      toast.error(apiErrorMessage(err, 'Failed to create geofence'));
    },
  });

  const updateGeofenceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => geofenceApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      toast.success('Geofence updated successfully');
      setShowEditModal(false);
      setEditingGeofence(null);
      setForm(initialForm);
    },
    onError: (err: any) => {
      toast.error(apiErrorMessage(err, 'Failed to update geofence'));
    },
  });

  const deleteGeofenceMutation = useMutation({
    mutationFn: (id: string) => geofenceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      toast.success('Geofence deleted');
    },
    onError: () => {
      toast.error('Failed to delete geofence');
    },
  });

  const geofences = (data?.geofences || []) as any[];

  // One section per type that actually has geofences (respecting the active filter).
  const sections = GEOFENCE_TYPES
    .filter((t) => typeFilter === '' || typeFilter === t)
    .map((t) => ({ type: t, items: geofences.filter((g) => g.type === t) }))
    .filter((s) => s.items.length > 0);

  const handleGeofenceClick = (geofence: any) => {
    setSelectedGeofence(geofence);
    setShowDetailModal(true);
  };

  const handleDelete = (geofence: any) => {
    if (window.confirm(`Delete "${geofence.name}"? This action cannot be undone.`)) {
      deleteGeofenceMutation.mutate(geofence.id);
    }
  };

  const handleEdit = (geofence: any) => {
    setEditingGeofence(geofence);
    setForm({
      name: geofence.name || '',
      type: (geofence.type as GeofenceType) || 'FARMBASE',
      description: geofence.description || '',
      centerLat: geofence.centerLat?.toString() || '',
      centerLon: geofence.centerLon?.toString() || '',
      radiusMeters: geofence.radiusMeters?.toString() || '100',
      alertOnEntry: geofence.alertOnEntry || false,
      alertOnExit: geofence.alertOnExit || false,
    });
    setShowEditModal(true);
  };

  const handleCreateGeofence = () => {
    if (!form.name || !form.centerLat || !form.centerLon) {
      toast.error('Please fill all required fields');
      return;
    }
    createGeofenceMutation.mutate({
      shape: 'circle',
      name: form.name,
      type: form.type,
      description: form.description,
      centerLat: parseFloat(form.centerLat),
      centerLon: parseFloat(form.centerLon),
      radiusMeters: parseInt(form.radiusMeters) || 100,
      alertOnEntry: form.alertOnEntry,
      alertOnExit: form.alertOnExit,
      isActive: true,
    });
  };

  const handleUpdateGeofence = () => {
    if (!editingGeofence || !form.name) {
      toast.error('Please fill all required fields');
      return;
    }
    // Field-dropped pins are rename-only: their geometry stays as the field set it.
    if (!isFieldType(form.type) && (!form.centerLat || !form.centerLon)) {
      toast.error('Please fill all required fields');
      return;
    }

    updateGeofenceMutation.mutate({
      id: editingGeofence.id,
      data: {
        name: form.name,
        description: form.description,
        ...(isFieldType(form.type)
          ? {}
          : {
              centerLat: parseFloat(form.centerLat),
              centerLon: parseFloat(form.centerLon),
              radiusMeters: parseInt(form.radiusMeters) || 100,
              alertOnEntry: form.alertOnEntry,
              alertOnExit: form.alertOnExit,
            }),
      },
    });
  };

  const renderGeofenceForm = (isEdit: boolean) => {
    // In edit mode, field-dropped pins lock their geometry (rename-only).
    const geometryLocked = isEdit && isFieldType(form.type);
    return (
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none"
            placeholder="e.g., Alpha Farmbase"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Type *</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as GeofenceType })}
            className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none disabled:opacity-60"
            disabled={isEdit}
          >
            {(isEdit ? GEOFENCE_TYPES : CREATABLE_TYPES).map((t) => (
              <option key={t} value={t}>
                {GEOFENCE_META[t].emoji} {GEOFENCE_META[t].label}
              </option>
            ))}
          </select>
        </div>

        {geometryLocked && (
          <div className="p-3 bg-command-accent/10 border border-command-accent/30 rounded-lg text-xs text-command-accent">
            Field-dropped {GEOFENCE_META[form.type].label} — geometry is set live from the field. You can rename it here.
          </div>
        )}

        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none"
            rows={2}
            placeholder="Optional notes"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Latitude *</label>
            <input
              type="text"
              value={form.centerLat}
              onChange={(e) => setForm({ ...form, centerLat: e.target.value })}
              disabled={geometryLocked}
              className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none disabled:opacity-60"
              placeholder="Latitude"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Longitude *</label>
            <input
              type="text"
              value={form.centerLon}
              onChange={(e) => setForm({ ...form, centerLon: e.target.value })}
              disabled={geometryLocked}
              className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none disabled:opacity-60"
              placeholder="Longitude"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Radius (meters)</label>
          <input
            type="number"
            value={form.radiusMeters}
            onChange={(e) => setForm({ ...form, radiusMeters: e.target.value })}
            disabled={geometryLocked}
            className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-white focus:border-command-accent focus:outline-none disabled:opacity-60"
            placeholder="100"
          />
        </div>

        {!geometryLocked && (
          <div className="p-4 bg-command-bg/60 border border-command-border rounded-lg space-y-3">
            <p className="text-xs text-command-accent font-semibold">GEOFENCE ALERTS (Entry/Exit will trigger alerts)</p>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={form.alertOnEntry}
                  onChange={(e) => setForm({ ...form, alertOnEntry: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-command-bg"
                />
                Alert on Entry
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={form.alertOnExit}
                  onChange={(e) => setForm({ ...form, alertOnExit: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-command-bg"
                />
                Alert on Exit
              </label>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <button
            onClick={() => {
              if (isEdit) {
                setShowEditModal(false);
                setEditingGeofence(null);
              } else {
                setShowCreateModal(false);
              }
              setForm(initialForm);
            }}
            className="flex-1 py-3 bg-command-bg border border-command-border rounded-lg text-gray-300 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={isEdit ? handleUpdateGeofence : handleCreateGeofence}
            disabled={isEdit ? updateGeofenceMutation.isPending : createGeofenceMutation.isPending}
            className="flex-1 py-3 bg-command-accent text-command-bg rounded-lg font-bold hover:bg-command-accent/90 disabled:opacity-50"
          >
            {isEdit
              ? (updateGeofenceMutation.isPending ? 'Updating...' : 'Update Geofence')
              : (createGeofenceMutation.isPending ? 'Creating...' : 'Create Geofence')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-bold text-gray-100">Geofences</h1>
          <p className="text-gray-500 text-sm">Operational zones and control measures — click a card to view on the live map</p>
          <p className="text-xs text-amber-500 mt-1">⚠️ Changes are saved to the database. Export config before reseeding.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Export Config Button */}
          <button
            onClick={() => {
              const exportData = {
                exportedAt: new Date().toISOString(),
                geofences: geofences.map((g: any) => ({
                  name: g.name,
                  description: g.description,
                  type: g.type,
                  centerLat: g.centerLat,
                  centerLon: g.centerLon,
                  radiusMeters: g.radiusMeters,
                  polygonCoords: g.polygonCoords,
                  alertOnEntry: g.alertOnEntry,
                  alertOnExit: g.alertOnExit,
                  metadata: g.metadata,
                })),
              };
              const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `geofence-config-${new Date().toISOString().split('T')[0]}.json`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success('Geofence configuration exported!');
            }}
            className="flex items-center gap-2 px-3 py-2 min-h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors touch-manipulation"
            title="Export geofence configuration to JSON file"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export Config
          </button>

          {canManage && (
            <button
              onClick={() => { setForm(initialForm); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 min-h-11 bg-command-accent text-command-bg rounded-lg font-semibold hover:bg-command-accent/90 transition-colors touch-manipulation"
            >
              <PlusIcon className="w-5 h-5" />
              Create Geofence
            </button>
          )}
        </div>
      </div>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2">
        {['', ...GEOFENCE_TYPES].map((type) => (
          <button
            key={type || 'all'}
            onClick={() => setTypeFilter(type)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              typeFilter === type
                ? 'bg-command-accent text-command-bg'
                : 'bg-command-panel border border-command-border text-gray-400 hover:text-gray-200'
            )}
          >
            {type === ''
              ? 'All Types'
              : `${GEOFENCE_META[type as GeofenceType].emoji} ${GEOFENCE_META[type as GeofenceType].label}`}
          </button>
        ))}
      </div>

      {/* Sections (one per type present) */}
      {sections.map(({ type, items }, idx) => {
        const meta = GEOFENCE_META[type];
        const fieldDropped = isFieldType(type);
        return (
          <div key={type} className="space-y-4">
            <div className={clsx('flex items-center gap-3', idx > 0 && 'mt-8 pt-6 border-t border-command-border')}>
              <h2 className="text-lg font-semibold text-gray-200">{meta.emoji} {meta.label}</h2>
              <span className={clsx('px-2 py-0.5 rounded text-xs border', meta.badge)}>{items.length}</span>
              {fieldDropped && (
                <span className="text-xs text-command-accent">Field-dropped · read-only geometry</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((geofence: any) => (
                <GeofenceCard
                  key={geofence.id}
                  geofence={geofence}
                  canManage={canManage}
                  onClick={() => handleGeofenceClick(geofence)}
                  onEdit={canManage ? () => handleEdit(geofence) : undefined}
                  onDelete={canManage && !fieldDropped ? () => handleDelete(geofence) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}

      {(isLoading || error) && (
        <div className="text-center py-12">
          {error ? (
            <>
              <div className="text-red-400 text-lg mb-2">⚠️ Error Loading Geofences</div>
              <p className="text-gray-400 text-sm">{(error as any)?.response?.data?.error || (error as any)?.message || 'Failed to load geofence data'}</p>
              <button
                onClick={() => queryClient.invalidateQueries({ queryKey: ['geofences'] })}
                className="mt-4 px-4 py-2 bg-command-accent text-white rounded-lg hover:bg-command-accent/80"
              >
                Retry
              </button>
            </>
          ) : (
            <div className="w-8 h-8 border-2 border-command-accent border-t-transparent rounded-full animate-spin mx-auto" />
          )}
        </div>
      )}

      {!isLoading && geofences.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No geofences found
        </div>
      )}

      {/* Create Geofence Modal (HQ only) — bottom sheet on mobile */}
      {showCreateModal && canManage && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-command-panel border border-command-border rounded-t-2xl sm:rounded-xl w-full max-w-lg max-h-[92dvh] overflow-y-auto overscroll-contain m-0 sm:m-4">
            <div className="flex items-center justify-between p-4 border-b border-command-border">
              <h2 className="text-lg font-bold text-white">Create Geofence</h2>
              <button onClick={() => { setShowCreateModal(false); setForm(initialForm); }} className="p-1 hover:bg-command-bg rounded">
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {renderGeofenceForm(false)}
          </div>
        </div>
      )}

      {/* Edit Geofence Modal (HQ only) — bottom sheet on mobile */}
      {showEditModal && editingGeofence && canManage && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-command-panel border border-command-border rounded-t-2xl sm:rounded-xl w-full max-w-lg max-h-[92dvh] overflow-y-auto overscroll-contain m-0 sm:m-4">
            <div className="flex items-center justify-between p-4 border-b border-command-border">
              <h2 className="text-lg font-bold text-white">Edit Geofence</h2>
              <button onClick={() => { setShowEditModal(false); setEditingGeofence(null); setForm(initialForm); }} className="p-1 hover:bg-command-bg rounded">
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {renderGeofenceForm(true)}
          </div>
        </div>
      )}

      {/* Geofence Detail Modal */}
      {showDetailModal && selectedGeofence && (
        <GeofenceDetailModal
          geofence={selectedGeofence}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedGeofence(null);
          }}
          onEdit={() => {
            setShowDetailModal(false);
            handleEdit(selectedGeofence);
          }}
          onViewMap={() => {
            if (selectedGeofence.centerLat != null && selectedGeofence.centerLon != null) {
              navigate(`/map?lat=${selectedGeofence.centerLat}&lon=${selectedGeofence.centerLon}&zoom=16&name=${encodeURIComponent(selectedGeofence.name)}`);
            }
          }}
          canEdit={canManage}
        />
      )}
    </div>
  );
}

// Geofence Detail Modal Component
function GeofenceDetailModal({
  geofence,
  onClose,
  onEdit,
  onViewMap,
  canEdit,
}: {
  geofence: any;
  onClose: () => void;
  onEdit: () => void;
  onViewMap: () => void;
  canEdit: boolean;
}) {
  const type = geofence.type as GeofenceType;
  const meta = GEOFENCE_META[type] ?? GEOFENCE_META.BOUNDARY;
  const fieldDropped = isFieldType(type);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-command-panel border border-command-border rounded-t-2xl sm:rounded-xl w-full max-w-2xl max-h-[92dvh] overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="sticky top-0 bg-command-panel border-b border-command-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center border text-2xl', meta.badge)}>
              {meta.emoji}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{geofence.name}</h2>
              <p className="text-sm text-gray-400">{meta.label}{fieldDropped ? ' · field-dropped' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button
                onClick={onEdit}
                className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg flex items-center gap-2 transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
                {fieldDropped ? 'Rename' : 'Edit'}
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-command-bg rounded-lg">
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Basic Information</h3>
            <div className="bg-command-bg/50 rounded-lg p-4 space-y-3">
              <DetailRow label="Type" value={meta.label} />
              <DetailRow label="Description" value={geofence.description || 'N/A'} />
              <DetailRow
                label="Status"
                value={geofence.isActive ? 'Active' : 'Inactive'}
                valueClassName={geofence.isActive ? 'text-green-400' : 'text-gray-400'}
              />
              <DetailRow
                label="Alerts on Entry"
                value={geofence.alertOnEntry ? 'Enabled' : 'Disabled'}
                valueClassName={geofence.alertOnEntry ? 'text-green-400' : 'text-gray-400'}
              />
              <DetailRow
                label="Alerts on Exit"
                value={geofence.alertOnExit ? 'Enabled' : 'Disabled'}
                valueClassName={geofence.alertOnExit ? 'text-green-400' : 'text-gray-400'}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Location</h3>
            <div className="bg-command-bg/50 rounded-lg p-4 space-y-3">
              <DetailRow
                label="Coordinates"
                value={
                  geofence.centerLat != null && geofence.centerLon != null
                    ? `${geofence.centerLat.toFixed(6)}°N, ${geofence.centerLon.toFixed(6)}°E`
                    : geofence.polygonCoords ? 'Polygon' : 'N/A'
                }
                valueClassName="font-mono text-sm"
              />
              <DetailRow
                label="Radius"
                value={geofence.radiusMeters ? `${geofence.radiusMeters} meters` : 'N/A'}
              />
            </div>
          </div>

          {fieldDropped && (
            <div className="p-3 bg-command-accent/10 border border-command-accent/30 rounded-lg text-xs text-command-accent">
              This {meta.label} was dropped live from the field. Its geometry is read-only in this editor.
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-command-border">
            <button
              onClick={onViewMap}
              className="flex-1 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              View on Live Map
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for detail rows
function DetailRow({
  label,
  value,
  valueClassName = 'text-white',
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-gray-400 flex-shrink-0">{label}:</span>
      {typeof value === 'string' ? (
        <span className={clsx('text-sm font-medium text-right', valueClassName)}>{value}</span>
      ) : (
        <div className={valueClassName}>{value}</div>
      )}
    </div>
  );
}
